import { useNavigate, useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import useDashboardStats from "../../hooks/useDashboardStats";
import useLinkedUploadAgencies from "../../hooks/useLinkedUploadAgencies";
import DashboardStatsBar from "../dashboard/DashboardStatsBar";
import { withReturnPath } from "../../utils/navReturn";
import "../../styles/dashboard.css";

function formatCount(n) {
  const num = Number(n) || 0;
  return num.toLocaleString("en-IN");
}

const HOME_CONFIRMATION_ITEMS = [
  {
    key: "pending",
    label: "Pending confirmations",
    statKey: "pendingConfirmations",
    icon: "⏳",
    desc: "Review pending field confirmations",
  },
  {
    key: "confirmations",
    label: "Confirmed cases",
    statKey: "confirmations",
    icon: "✅",
    desc: "View confirmed vehicle traces",
  },
  {
    key: "inventory",
    label: "Seized vehicles Details",
    statKey: "inventoryConfirmed",
    icon: "🚘",
    desc: "Seized vehicles in inventory",
  },
];

export default function FindVehiclesStatsPanel({
  className = "fv-stats-panel home-field-stats",
  returnPathFallback = "/find-vehicles",
  arrangeHome = false,
  homeSlots = null,
}) {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isRepoAdmin = auth?.user?.role === "REPO_ADMIN";
  const { stats, loading, error } = useDashboardStats();
  const {
    companyTotalRecords,
    companyOwnRecords,
    allBanksNbfcsRecords,
    loading: sourcesLoading,
    error: sourcesError,
    reload: reloadSources,
  } = useLinkedUploadAgencies();

  const handleStatClick = (key) => {
    const returnTo =
      `${location.pathname}${location.search}` || returnPathFallback;

    if (key === "pending") {
      navigate(withReturnPath("/confirmation?status=PENDING", returnTo));
      return;
    }

    if (key === "confirmations") {
      navigate(withReturnPath("/confirmation?status=CONFIRMED", returnTo));
      return;
    }

    if (key === "inventory") {
      navigate(withReturnPath("/seized-vehicles", returnTo));
      return;
    }

    if (key === "records") {
      if (isRepoAdmin) {
        navigate(withReturnPath("/bank-details", returnTo));
      } else {
        navigate("/find-vehicles");
      }
    }
  };

  const handleRecordStatClick = (key) => {
    const returnTo =
      `${location.pathname}${location.search}` || returnPathFallback;

    if (key === "agency") {
      if (isRepoAdmin) {
        navigate(withReturnPath("/bank-details", returnTo));
        return;
      }
      navigate("/find-vehicles");
      return;
    }

    if (key === "banks") {
      if (isRepoAdmin) {
        navigate(withReturnPath("/bank-records", returnTo));
        return;
      }
      navigate("/find-vehicles");
      return;
    }

    navigate(isRepoAdmin ? withReturnPath("/find-vehicles", returnTo) : "/find-vehicles");
  };

  const recordItems = [
    {
      key: "agency",
      label: "Agency Upload Repo List Details",
      icon: "📁",
      desc: "Agency uploaded repo records",
      value: formatCount(companyOwnRecords),
    },
    {
      key: "banks",
      label: "All Banks / NBFCs Uploaded Records",
      icon: "🏦",
      desc: "Records uploaded by linked banks",
      value: formatCount(allBanksNbfcsRecords),
    },
    {
      key: "allRecords",
      label: "All Records",
      icon: "📊",
      desc: "Agency plus banks/NBFC records",
      value: formatCount(companyTotalRecords),
    },
  ];

  const renderUploadSourceCards = () => {
    if (sourcesError) {
      return (
        <div className="fv-agency-upload-empty fv-agency-upload-empty--error">
          <p className="error-text">{sourcesError}</p>
          <button type="button" className="secondary-page-btn" onClick={reloadSources}>
            Retry
          </button>
        </div>
      );
    }

    return (
      <DashboardStatsBar
        className="home-hero__stats"
        variant="field"
        items={recordItems}
        loading={sourcesLoading}
        activeKey=""
        onStatClick={handleRecordStatClick}
      />
    );
  };

  if (arrangeHome) {
    const confirmationItems = HOME_CONFIRMATION_ITEMS.map((item) => ({
      key: item.key,
      label: item.label,
      icon: item.icon,
      desc: item.desc,
      value: stats[item.statKey] ?? 0,
    }));

    return (
      <section className={className} aria-label="Upload and confirmation stats">
        {error ? <p className="error-text fv-stats-error">{error}</p> : null}
        <div className="stats stats-single-row dashboard-stats-bar dashboard-stats-bar--field-user home-starter-cards home-dashboard-board home-hero__stats">
          <DashboardStatsBar
            embedded
            variant="field"
            items={confirmationItems.slice(0, 2)}
            loading={loading}
            activeKey=""
            onStatClick={handleStatClick}
          />
          {homeSlots?.afterConfirmed || null}
          <DashboardStatsBar
            embedded
            variant="field"
            items={confirmationItems.slice(2)}
            loading={loading}
            activeKey=""
            onStatClick={handleStatClick}
          />
          {homeSlots?.afterInventory || null}
          {sourcesError ? null : (
            <DashboardStatsBar
              embedded
              variant="field"
              items={recordItems}
              loading={sourcesLoading}
              activeKey=""
              onStatClick={handleRecordStatClick}
            />
          )}
        </div>
        {sourcesError ? (
          <div className="fv-agency-upload-empty fv-agency-upload-empty--error">
            <p className="error-text">{sourcesError}</p>
            <button type="button" className="secondary-page-btn" onClick={reloadSources}>
              Retry
            </button>
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <section
      className={className}
      aria-label="Upload and confirmation stats"
    >
      {error ? <p className="error-text fv-stats-error">{error}</p> : null}

      <DashboardStatsBar
        className="home-hero__stats"
        variant="field"
        stats={stats}
        loading={loading}
        activeKey=""
        onStatClick={handleStatClick}
      />

      {renderUploadSourceCards()}
    </section>
  );
}
